/**
 * SchedulesDashboard.tsx
 * The Persistent Monitor Hub — full-page dashboard for managing
 * scheduled workflows, viewing logs, and handling approvals.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Icons, getAppIcon } from '../components/ui/Icons';
import { ScheduledWorkflow, WorkflowStatus, ParsedSchedule } from '../types';
import { api, isAuthMockMode } from '../services/api';
import { supabase } from '../lib/supabaseClient';
import { cronToHumanReadable } from '../services/scheduleParser';
import SchedulePromptBar from '../features/scheduling/components/SchedulePromptBar';
import ScheduleBlueprintCard from '../features/scheduling/components/ScheduleBlueprintCard';
import ScheduleWorkflowCard from '../features/scheduling/components/ScheduleWorkflowCard';

const getWorkflowStepOutput = (workflow: ScheduledWorkflow, step: any): string => {
  return `No execution output recorded yet. Run the schedule to generate real output.`;
};

const formatMarkdownText = (text: string): string => {
  let html = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // Restore HTML tags for strong and em
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
  
  // Placeholders array to prevent raw URL regex matching inside HTML tags
  const placeholders: string[] = [];

  // 1. Parse markdown links [text](url) and save them as placeholders
  html = html.replace(/\[(.*?)\]\((https?:\/\/.*?)\)/g, (match, linkText, url) => {
    const placeholder = `___LINK_PLACEHOLDER_${placeholders.length}___`;
    placeholders.push(`<a href="${url}" target="_blank" rel="noopener noreferrer" style="color: #2563eb; font-weight: 600; text-decoration: underline;">${linkText}</a>`);
    return placeholder;
  });

  // 2. Convert remaining raw URLs (like spreadsheet links, doc links, notion links, calendar htmlLinks) to clickable links
  html = html.replace(/(\bhttps?:\/\/[^\s<>\(\)]+)/g, (url) => {
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color: #2563eb; font-weight: 600; text-decoration: underline;">${url}</a>`;
  });

  // 3. Restore markdown link placeholders
  placeholders.forEach((placeholderHtml, idx) => {
    html = html.replace(`___LINK_PLACEHOLDER_${idx}___`, placeholderHtml);
  });

  return html;
};

const isRetrievalTool = (toolName: string): boolean => {
  const name = (toolName || '').toLowerCase();
  return name.startsWith('list_') || 
         name.startsWith('read_') || 
         name.startsWith('search_') || 
         name.startsWith('get_') ||
         name.includes('fetch') ||
         name.includes('find');
};

const getCondensedSummary = (toolName: string): string => {
  const name = (toolName || '').toLowerCase();
  if (name.includes('email') || name.includes('gmail')) {
    return 'Retrieved emails successfully for processing.';
  }
  if (name.includes('calendar') || name.includes('event')) {
    return 'Fetched calendar events successfully for processing.';
  }
  if (name.includes('sheet') || name.includes('spreadsheet') || name.includes('read_sheet')) {
    return 'Read spreadsheet values successfully for processing.';
  }
  if (name.includes('doc')) {
    return 'Fetched document content successfully for processing.';
  }
  if (name.includes('notion')) {
    return 'Fetched notion content successfully for processing.';
  }
  return 'Fetched data successfully for processing.';
};

const renderFormattedOutput = (outputText: string): React.ReactNode => {
  if (!outputText) {
    return <div className="text-gray-400 italic text-left">No output recorded.</div>;
  }

  // 1. Try to parse JSON
  let parsed: any = null;
  try {
    const trimmed = outputText.trim();
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      try {
        parsed = JSON.parse(trimmed);
      } catch {
        // Try Python representation conversion (e.g. single quotes to double quotes)
        let normalized = trimmed
          .replace(/'/g, '"')
          .replace(/True/g, 'true')
          .replace(/False/g, 'false')
          .replace(/None/g, 'null');
        parsed = JSON.parse(normalized);
      }
    }
  } catch (e) {
    // Keep parsed as null
  }

  // 2. Format based on parsed JSON
  if (parsed) {
    if (Array.isArray(parsed)) {
      if (parsed.length === 0) {
        return <div className="text-gray-500 italic text-left">No records found.</div>;
      }

      const firstItem = parsed[0] || {};
      
      // Email Array representation
      const isEmail = 'from' in firstItem || 'subject' in firstItem || 'snippet' in firstItem;
      if (isEmail) {
        return (
          <div className="space-y-2 mt-1">
            {parsed.map((email: any, i: number) => (
              <div key={i} className="p-3 bg-gray-50 dark:bg-[#171717] border border-gray-100 dark:border-gray-800 rounded-xl flex flex-col gap-1 text-left">
                <div className="flex justify-between items-start gap-2">
                  <span className="font-bold text-gray-900 dark:text-white text-xs truncate max-w-[70%]">
                    {email.from || 'Unknown Sender'}
                  </span>
                  {email.date && (
                    <span className="text-[10px] text-gray-400 shrink-0 font-mono">
                      {email.date}
                    </span>
                  )}
                </div>
                {email.subject && (
                  <div className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                    {email.subject}
                  </div>
                )}
                {email.snippet && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mt-0.5 leading-relaxed">
                    {email.snippet}
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      }

      // Calendar Events Array representation
      const isEvent = 'summary' in firstItem || 'start' in firstItem || 'description' in firstItem;
      if (isEvent) {
        return (
          <div className="space-y-2 mt-1">
            {parsed.map((event: any, i: number) => {
              const startVal = event.start;
              const dateStr = startVal 
                ? (typeof startVal === 'object' ? (startVal.dateTime || startVal.date) : startVal)
                : '';
              return (
                <div key={i} className="p-3 bg-gray-50 dark:bg-[#171717] border border-gray-100 dark:border-gray-800 rounded-xl flex flex-col gap-1 text-left">
                  <div className="flex justify-between items-start gap-2">
                    <span className="font-bold text-gray-900 dark:text-white text-xs truncate max-w-[70%]">
                      {event.summary || '(No Title)'}
                    </span>
                    {dateStr && (
                      <span className="text-[10px] text-gray-400 shrink-0 font-mono">
                        {new Date(dateStr).toLocaleString('en-US', {
                          month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}
                      </span>
                    )}
                  </div>
                  {event.description && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mt-0.5 leading-relaxed">
                      {event.description}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      }

      // Generic Array
      return (
        <div className="space-y-2 mt-1">
          {parsed.map((item: any, i: number) => (
            <div key={i} className="p-2 bg-gray-50 dark:bg-[#171717] border border-gray-100 dark:border-gray-800 rounded-lg text-xs font-mono text-left overflow-x-auto whitespace-pre-wrap dark:text-gray-300">
              {typeof item === 'object' ? JSON.stringify(item, null, 2) : String(item)}
            </div>
          ))}
        </div>
      );
    }

    // Generic Object representation
    return (
      <pre className="text-xs text-gray-600 dark:text-gray-300 font-mono bg-gray-50 dark:bg-[#171717] p-3 rounded-lg overflow-x-auto text-left whitespace-pre-wrap mt-1 border dark:border-gray-800">
        {JSON.stringify(parsed, null, 2)}
      </pre>
    );
  }

  // 3. Fallback to Markdown/Plain Text formatting
  const isTable = outputText.includes('|');
  return (
    <div 
      className={`text-xs text-gray-600 dark:text-gray-300 bg-white dark:bg-[#212121] border border-gray-100 dark:border-gray-800 rounded-lg p-3 leading-relaxed whitespace-pre-wrap max-h-[500px] overflow-y-auto custom-scrollbar text-left mt-1 ${isTable ? 'font-mono' : 'font-medium font-sans'}`}
      dangerouslySetInnerHTML={{ __html: formatMarkdownText(outputText) }}
    />
  );
};

interface SchedulesDashboardProps {
  onClose: () => void;
  prefillPrompt?: string;
  onClearPrefill?: () => void;
  isActive?: boolean;
}

type TabId = 'active' | 'logs' | 'pending';

const TABS: { id: TabId; label: string; icon: React.ReactNode }[] = [
  { id: 'active', label: 'Active', icon: <Icons.Zap className="w-4 h-4" /> },
  { id: 'logs', label: 'Logs', icon: <Icons.Logs className="w-4 h-4" /> },
  { id: 'pending', label: 'Pending', icon: <Icons.Clock className="w-4 h-4" /> },
];

const SchedulesDashboard: React.FC<SchedulesDashboardProps> = ({ onClose, prefillPrompt, onClearPrefill, isActive }) => {
  const [activeTab, setActiveTab] = useState<TabId>('active');
  const [workflows, setWorkflows] = useState<ScheduledWorkflow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [showCreator, setShowCreator] = useState(!!prefillPrompt);
  const [isParsing, setIsParsing] = useState(false);
  const [parsedSchedule, setParsedSchedule] = useState<ParsedSchedule | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  const [logToDelete, setLogToDelete] = useState<{ workflowId: string; runId: string } | null>(null);

  useEffect(() => {
    if (prefillPrompt) {
      setShowCreator(true);
      setParsedSchedule(null);
    }
  }, [prefillPrompt]);

  // Modals & Forms State
  const [workflowToDelete, setWorkflowToDelete] = useState<ScheduledWorkflow | null>(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState<ScheduledWorkflow | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Edit Form Fields
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editCron, setEditCron] = useState('');
  const [editFrequency, setEditFrequency] = useState('daily');
  const [editTime, setEditTime] = useState('09:00');
  const [editDayOfWeek, setEditDayOfWeek] = useState('1');

  // ─── Fetch Workflows ───
  const fetchWorkflows = useCallback(async () => {
    try {
      const data = await api.getSchedules();
      setWorkflows(data);
    } catch (e) {
      console.error('[SchedulesDashboard] Failed to fetch:', e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isActive) {
      fetchWorkflows();
    }
  }, [isActive, fetchWorkflows]);

  // ─── Actions ───
  const handlePauseResume = async (id: string, newStatus: WorkflowStatus) => {
    // Optimistic update
    setWorkflows(prev => prev.map(w => w.id === id ? { ...w, status: newStatus } : w));
    try {
      await api.updateScheduleStatus(id, newStatus);
    } catch (e) {
      console.error('[SchedulesDashboard] Status update failed:', e);
      fetchWorkflows(); // Revert on failure
    }
  };

  const handleDelete = async (id: string) => {
    // Optimistic delete
    setWorkflows(prev => prev.filter(w => w.id !== id));
    try {
      await api.deleteSchedule(id);
    } catch (e) {
      console.error('[SchedulesDashboard] Delete failed:', e);
      fetchWorkflows();
    }
  };

  const handleApprovePendingStep = async (workflowId: string, stepId: string, runId: string) => {
    setApprovingId(runId);
    try {
      // Find the workflow and step
      const workflow = workflows.find(w => w.id === workflowId);
      if (!workflow) return;
      const step = workflow.steps.find(s => s.id === stepId);
      if (!step) return;

      // Parse args and execution_history
      let parsedArgs = { ...(step.args || {}) };
      const history = Array.isArray(parsedArgs.execution_history) ? [...parsedArgs.execution_history] : [];
      const entryIdx = history.findIndex((h: any) => h.run_id === runId && h.status === 'pending');
      if (entryIdx === -1) return;

      // Update status to 'approved'
      history[entryIdx] = {
        ...history[entryIdx],
        status: 'approved',
      };

      const updatedArgs = {
        ...parsedArgs,
        execution_history: history,
      };

      // Save to Supabase
      if (isAuthMockMode) {
        setWorkflows(prev => prev.map(w => {
          if (w.id !== workflowId) return w;
          return {
            ...w,
            steps: w.steps.map(s => s.id === stepId ? { ...s, args: updatedArgs } : s),
          };
        }));
      } else {
        const { error } = await supabase!
          .from('workflow_steps')
          .update({ args: updatedArgs })
          .eq('id', stepId);
        if (error) throw error;
        
        // Also force-update scheduled_workflows next_run_at to now() to trigger immediate execution!
        const { error: wError } = await supabase!
          .from('scheduled_workflows')
          .update({ next_run_at: new Date().toISOString() })
          .eq('id', workflowId);
        if (wError) console.error('Failed to trigger immediate execute:', wError);
      }

      await fetchWorkflows();
    } catch (e) {
      console.error('[SchedulesDashboard] Failed to approve step:', e);
      alert('Failed to approve action. Please try again.');
    } finally {
      setApprovingId(null);
    }
  };

  const handleRejectPendingStep = async (workflowId: string, stepId: string, runId: string) => {
    setApprovingId(runId);
    try {
      // Find the workflow and step
      const workflow = workflows.find(w => w.id === workflowId);
      if (!workflow) return;
      const step = workflow.steps.find(s => s.id === stepId);
      if (!step) return;

      // Helper to compute next run
      const computeNextRun = (cron: string): string => {
        const now = new Date();
        const parts = cron.split(' ');
        if (parts.length !== 5) return now.toISOString();
        const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;
        const next = new Date(now);
        if (minute !== '*' && hour === '*') {
          next.setMinutes(parseInt(minute, 10), 0, 0);
          if (next <= now) next.setHours(next.getHours() + 1);
        } else if (hour !== '*' && minute !== '*') {
          next.setHours(parseInt(hour, 10), parseInt(minute, 10), 0, 0);
          if (next <= now) {
            if (dayOfWeek !== '*') {
              const targetDay = parseInt(dayOfWeek, 10);
              let daysUntil = targetDay - now.getDay();
              if (daysUntil <= 0) daysUntil += 7;
              next.setDate(next.getDate() + daysUntil);
            } else {
              next.setDate(next.getDate() + 1);
            }
          }
        }
        return next.toISOString();
      };

      // Parse args and execution_history
      let parsedArgs = { ...(step.args || {}) };
      const history = Array.isArray(parsedArgs.execution_history) ? [...parsedArgs.execution_history] : [];
      const entryIdx = history.findIndex((h: any) => h.run_id === runId && h.status === 'pending');
      if (entryIdx === -1) return;

      // Update status to 'rejected'
      history[entryIdx] = {
        ...history[entryIdx],
        status: 'rejected',
        result: 'Action was rejected by user.',
      };

      const updatedArgs = {
        ...parsedArgs,
        execution_history: history,
      };

      const nextRun = computeNextRun(workflow.cron_expression || "0 9 * * *");

      if (isAuthMockMode) {
        setWorkflows(prev => prev.map(w => {
          if (w.id !== workflowId) return w;
          return {
            ...w,
            status: 'failed',
            next_run_at: nextRun,
            steps: w.steps.map(s => s.id === stepId ? { ...s, args: updatedArgs } : s),
          };
        }));
      } else {
        const { error } = await supabase!
          .from('workflow_steps')
          .update({ args: updatedArgs })
          .eq('id', stepId);
        if (error) throw error;

        const { error: wError } = await supabase!
          .from('scheduled_workflows')
          .update({ 
            status: 'failed', 
            next_run_at: nextRun,
            last_run_at: new Date().toISOString()
          })
          .eq('id', workflowId);
        if (wError) throw wError;
      }

      await fetchWorkflows();
    } catch (e) {
      console.error('[SchedulesDashboard] Failed to reject step:', e);
      alert('Failed to reject action. Please try again.');
    } finally {
      setApprovingId(null);
    }
  };

  // ─── Log History Runs Computing ───
  const allRuns = React.useMemo(() => {
    const runs: Array<{
      id: string;
      workflowId: string;
      workflowName: string;
      workflowStatus: string;
      runAt: string;
      status: 'success' | 'failed' | 'pending';
      summary?: string;
      steps: Array<{
        id: string;
        step_order: number;
        tool_name: string;
        status: 'success' | 'failed' | 'skipped' | 'pending' | 'approved' | 'rejected' | 'executed' | 'auth_required';
        outputText: string;
        args: any;
      }>;
    }> = [];

    (workflows || []).forEach(workflow => {
      const steps = workflow.steps || [];
      const runsMap = new Map<string, { runAt: string; status: 'success' | 'failed' | 'pending'; stepsMap: Map<number, any> }>();

      steps.forEach(step => {
        let parsedArgs: any = {};
        try {
          parsedArgs = typeof step.args === 'string' ? JSON.parse(step.args) : step.args;
        } catch {
          parsedArgs = step.args || {};
        }

        const history = Array.isArray(parsedArgs?.execution_history) ? [...parsedArgs.execution_history] : [];

        // Support fallback legacy log entry if no execution_history exists yet but we have last_run_at
        if (history.length === 0 && parsedArgs?.last_result) {
          const fallbackRunId = `fallback-${workflow.id}-${workflow.last_run_at || workflow.created_at}`;
          history.push({
            run_id: fallbackRunId,
            run_at: workflow.last_run_at || workflow.created_at || new Date().toISOString(),
            result: parsedArgs.last_result,
            status: workflow.status === 'failed' ? 'failed' : 'success'
          });
        }

        history.forEach((h: any) => {
          if (!h.run_id) return;
          if (!runsMap.has(h.run_id)) {
            runsMap.set(h.run_id, {
              runAt: h.run_at || new Date().toISOString(),
              status: 'success',
              stepsMap: new Map()
            });
          }
          const runData = runsMap.get(h.run_id)!;
          if (h.status === 'failed') {
            runData.status = 'failed';
          } else if (h.status === 'pending' && runData.status !== 'failed') {
            runData.status = 'pending';
          }
          runData.stepsMap.set(step.step_order, {
            id: step.id,
            step_order: step.step_order,
            tool_name: step.tool_name,
            status: h.status || 'success',
            outputText: h.result || '',
            args: parsedArgs
          });
        });
      });

      runsMap.forEach((runData, runId) => {
        const runSteps: any[] = [];
        let runSummary = '';
        steps.forEach(step => {
          const stepRun = runData.stepsMap.get(step.step_order);
          if (stepRun) {
            runSteps.push(stepRun);
            const history = Array.isArray(stepRun.args?.execution_history) ? stepRun.args.execution_history : [];
            const entry = history.find((h: any) => h.run_id === runId);
            if (entry?.summary) {
              runSummary = entry.summary;
            }
          } else {
            runSteps.push({
              id: step.id,
              step_order: step.step_order,
              tool_name: step.tool_name,
              status: 'skipped',
              outputText: 'Step skipped or not executed.',
              args: step.args
            });
          }
        });
        runSteps.sort((a, b) => a.step_order - b.step_order);

        runs.push({
          id: runId,
          workflowId: workflow.id,
          workflowName: workflow.name,
          workflowStatus: workflow.status,
          runAt: runData.runAt,
          status: runData.status,
          steps: runSteps,
          summary: runSummary
        });
      });
    });

    return runs.sort((a, b) => new Date(b.runAt).getTime() - new Date(a.runAt).getTime());
  }, [workflows]);

  const handleDeleteLogClick = (workflowId: string, runId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setLogToDelete({ workflowId, runId });
  };

  const executeDeleteLog = async (workflowId: string, runId: string) => {
    const workflow = workflows.find(w => w.id === workflowId);
    if (!workflow) return;

    // Optimistically update workflows state
    setWorkflows(prev => prev.map(w => {
      if (w.id !== workflowId) return w;
      return {
        ...w,
        steps: (w.steps || []).map(step => {
          let parsedArgs: any = {};
          try {
            parsedArgs = typeof step.args === 'string' ? JSON.parse(step.args) : step.args;
          } catch {
            parsedArgs = step.args || {};
          }
          if (parsedArgs.execution_history) {
            parsedArgs.execution_history = parsedArgs.execution_history.filter((h: any) => h.run_id !== runId);
          }
          if (runId.startsWith('fallback-')) {
            delete parsedArgs.last_result;
          }
          return {
            ...step,
            args: parsedArgs
          };
        })
      };
    }));

    try {
      await api.deleteWorkflowLog(workflowId, runId, workflow.steps);
    } catch (err) {
      console.error("[SchedulesDashboard] Failed to delete workflow log:", err);
      alert("Failed to delete log from database. Please try again.");
      fetchWorkflows();
    }
  };

  const handleSelectWorkflow = (workflow: ScheduledWorkflow, editMode = false) => {
    setSelectedWorkflow(workflow);
    setIsEditing(editMode);
    setEditName(workflow.name);
    setEditDescription(workflow.description || '');
    
    const cron = workflow.cron_expression || '0 9 * * *';
    setEditCron(cron);

    const parts = cron.trim().split(/\s+/);
    if (parts.length === 5) {
      const [min, hr, dom, mon, dow] = parts;
      if (min === '0' && hr === '*' && dom === '*' && mon === '*' && dow === '*') {
        setEditFrequency('hourly');
      } else if (dom === '*' && mon === '*' && dow === '*') {
        setEditFrequency('daily');
        setEditTime(`${hr.padStart(2, '0')}:${min.padStart(2, '0')}`);
      } else if (dom === '*' && mon === '*' && dow.length === 1 && !isNaN(parseInt(dow))) {
        setEditFrequency('weekly');
        setEditTime(`${hr.padStart(2, '0')}:${min.padStart(2, '0')}`);
        setEditDayOfWeek(dow);
      } else {
        setEditFrequency('custom');
      }
    } else {
      setEditFrequency('custom');
    }
  };

  const handleSaveChanges = async () => {
    if (!selectedWorkflow) return;
    setIsSubmitting(true);
    
    let cronToSave = editCron;
    if (editFrequency === 'hourly') {
      cronToSave = '0 * * * *';
    } else if (editFrequency === 'daily') {
      const [hr, min] = editTime.split(':');
      cronToSave = `${parseInt(min, 10)} ${parseInt(hr, 10)} * * *`;
    } else if (editFrequency === 'weekly') {
      const [hr, min] = editTime.split(':');
      cronToSave = `${parseInt(min, 10)} ${parseInt(hr, 10)} * * ${editDayOfWeek}`;
    }

    try {
      const nextRunAt = await api.updateSchedule(selectedWorkflow.id, {
        name: editName,
        description: editDescription,
        cron_expression: cronToSave,
      });

      // Update local state
      setWorkflows(prev => prev.map(w => {
        if (w.id === selectedWorkflow.id) {
          return {
            ...w,
            name: editName,
            description: editDescription,
            cron_expression: cronToSave,
            next_run_at: nextRunAt !== undefined ? nextRunAt : w.next_run_at,
          };
        }
        return w;
      }));

      // Update modal details
      setSelectedWorkflow(prev => prev ? {
        ...prev,
        name: editName,
        description: editDescription,
        cron_expression: cronToSave,
        next_run_at: nextRunAt !== undefined ? nextRunAt : prev.next_run_at,
      } : null);

      setIsEditing(false);
    } catch (e) {
      console.error('[SchedulesDashboard] Failed to save changes:', e);
      alert('Failed to save changes. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (id: string) => {
    const wf = workflows.find(w => w.id === id);
    if (wf) {
      setWorkflowToDelete(wf);
      setSelectedWorkflow(null); // Close details modal if open
    }
  };

  // ─── Schedule Creation Flow ───
  const handlePromptSubmit = async (text: string) => {
    setIsParsing(true);
    try {
      const parsed = await api.parseScheduleWithAI(text);
      setParsedSchedule(parsed);
    } catch (e) {
      console.error('[SchedulesDashboard] Parse failed:', e);
    } finally {
      setIsParsing(false);
    }
  };

  const handleConfirmSchedule = async (schedule: ParsedSchedule) => {
    setIsSubmitting(true);
    try {
      const created = await api.createSchedule({
        name: schedule.name,
        description: schedule.actions.map(a => a.description).join(' → '),
        type: schedule.trigger.type,
        cron_expression: schedule.trigger.cron_expression,
        steps: schedule.actions.map(a => ({
          tool_name: a.tool_name,
          args: a.args,
        })),
      });

      if (created) {
        setWorkflows(prev => [created, ...prev]);
      }

      setShowCreator(false);
      setParsedSchedule(null);
      onClearPrefill?.();
    } catch (e) {
      console.error('[SchedulesDashboard] Create failed:', e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelCreator = () => {
    setShowCreator(false);
    setParsedSchedule(null);
    setIsParsing(false);
    onClearPrefill?.();
  };

  // ─── Filtered Views ───
  const activeWorkflows = (workflows || []).filter(w => w?.status === 'active' || w?.status === 'paused');
  const failedWorkflows = (workflows || []).filter(w => w?.status === 'failed');

  // ─── Render ───
  return (
    <div className="w-full h-full flex flex-col bg-white dark:bg-[#050505] animate-in fade-in duration-300 relative" id="schedules-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between px-10 py-8 border-b border-gray-100 dark:border-white/5 flex-shrink-0">
        <h2 className="text-2xl font-normal text-gray-900 dark:text-white tracking-tight">Schedules</h2>
        <button
          onClick={onClose}
          className="p-2 text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#212121] rounded-full transition-all"
        >
          <Icons.X className="w-6 h-6" />
        </button>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto p-10 no-scrollbar">
        <div className="max-w-6xl mx-auto pb-12">
          
          {/* Sub-header Controls */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-1 bg-gray-50 dark:bg-[#171717] p-1 rounded-2xl border border-gray-100 dark:border-white/5">
              {TABS.map(tab => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition-all duration-200
                      ${isActive
                        ? 'bg-white text-black shadow-sm border-gray-200/50 dark:bg-[#050505] dark:text-white dark:border-white/5'
                        : 'bg-transparent text-gray-500 hover:text-gray-900 border-transparent dark:text-gray-400 dark:hover:text-white dark:border-transparent'
                      }
                    `}
                  >
                    {tab.icon}
                    <span>{tab.label}</span>
                    {tab.id === 'active' && activeWorkflows.length > 0 && (
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full transition-colors ${
                        isActive ? 'bg-black text-white dark:bg-white dark:text-black' : 'bg-gray-200 text-gray-600 dark:bg-[#212121] dark:text-gray-450'
                      }`}>
                        {activeWorkflows.length}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => { setShowCreator(true); setParsedSchedule(null); }}
              className="flex items-center justify-center gap-2 px-5 py-2.5 bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-200 rounded-full text-sm font-semibold hover:shadow-lg transition-all transform hover:scale-[1.01] active:scale-[0.99] shrink-0"
            >
              <Icons.Plus className="w-4 h-4" />
              <span>New Schedule</span>
            </button>
          </div>

          {/* Creator Overlay Card */}
          {showCreator && (
            <div className="bg-gray-50/50 dark:bg-[#171717]/50 border border-gray-100 dark:border-gray-800 rounded-2xl p-6 mb-8 animate-in fade-in slide-in-from-top-4 duration-300">
              {!parsedSchedule ? (
                <SchedulePromptBar
                  onSubmit={handlePromptSubmit}
                  onCancel={handleCancelCreator}
                  isLoading={isParsing}
                  initialValue={prefillPrompt || ''}
                />
              ) : (
                <ScheduleBlueprintCard
                  schedule={parsedSchedule}
                  onConfirm={handleConfirmSchedule}
                  onCancel={handleCancelCreator}
                  onEdit={() => setParsedSchedule(null)}
                  isSubmitting={isSubmitting}
                />
              )}
            </div>
          )}

          {/* Tab Contents */}
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className="w-10 h-10 border-3 border-gray-200 dark:border-gray-800 border-t-black dark:border-t-white rounded-full animate-spin"></div>
              <span className="text-sm text-gray-400 dark:text-gray-500 font-medium">Loading schedules...</span>
            </div>
          ) : activeTab === 'active' ? (
            // Active Workflows Grid
            activeWorkflows.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {activeWorkflows.map(workflow => (
                  <ScheduleWorkflowCard
                    key={workflow.id}
                    workflow={workflow}
                    onPauseResume={handlePauseResume}
                    onDelete={handleDeleteClick}
                    onSelect={handleSelectWorkflow}
                  />
                ))}
              </div>
            ) : (
              // Empty State
              <div className="flex flex-col items-center justify-center py-20 gap-6">
                <div className="w-16 h-16 rounded-2xl bg-gray-50 dark:bg-[#171717] border border-gray-100/60 dark:border-white/5 flex items-center justify-center">
                  <Icons.CalendarClock className="w-8 h-8 text-gray-300 dark:text-gray-600" />
                </div>
                <div className="text-center max-w-sm">
                  <h3 className="text-lg font-normal text-gray-900 dark:text-white mb-2">No active schedules</h3>
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    Create your first autonomous workflow. Neura will run it in the background while you focus on what matters.
                  </p>
                </div>
                <button
                  onClick={() => { setShowCreator(true); setParsedSchedule(null); }}
                  className="flex items-center gap-2 px-6 py-3 bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-200 rounded-full text-sm font-semibold hover:shadow-lg transition-all transform hover:scale-[1.01]"
                >
                  <Icons.Plus className="w-4 h-4" />
                  Create Your First Schedule
                </button>
              </div>
            )
          ) : activeTab === 'logs' ? (
            // Logs Tab
            <div className="space-y-4">
              {allRuns.filter(r => r.status !== 'pending').length > 0 ? (
                allRuns.filter(r => r.status !== 'pending').map(run => {
                  const isExpanded = expandedLogId === run.id;
                  return (
                    <div
                      key={run.id}
                      onClick={() => setExpandedLogId(prev => prev === run.id ? null : run.id)}
                      className="p-5 rounded-2xl border border-gray-100 dark:border-gray-800 hover:border-gray-250 dark:hover:border-gray-700 transition-all duration-300 bg-white dark:bg-[#212121] shadow-sm hover:shadow-md flex flex-col gap-4 cursor-pointer select-none"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 min-w-0">
                          <div className="relative flex-shrink-0 w-2.5 h-2.5">
                            <div className={`w-2.5 h-2.5 rounded-full ${run.status === 'failed' ? 'bg-gray-400 dark:bg-gray-600' : 'bg-black dark:bg-white'}`}></div>
                            {!isExpanded && run.status !== 'failed' && (
                              <div className="absolute inset-0 w-2.5 h-2.5 rounded-full animate-ping opacity-75 bg-gray-400 dark:bg-gray-500"></div>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-bold text-gray-900 dark:text-white truncate">{run.workflowName}</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                              {run.steps.filter(s => s.status !== 'skipped').length} step{run.steps.filter(s => s.status !== 'skipped').length !== 1 ? 's' : ''} executed
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 shrink-0 pl-4">
                          <div className="text-right">
                            <p className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                              {new Date(run.runAt).toLocaleString('en-US', {
                                month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                              })}
                            </p>
                            <p className={`text-[10px] font-bold uppercase tracking-wider mt-0.5 ${
                              run.status === 'failed' ? 'text-gray-400 dark:text-gray-600' : 'text-black dark:text-white'
                            }`}>
                              {run.status === 'failed' ? 'Failed' : 'Success'}
                            </p>
                          </div>

                          <Icons.ChevronDown className={`w-4 h-4 text-gray-400 dark:text-gray-500 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
                        </div>
                      </div>

                      {/* Collapsible log details */}
                      {isExpanded && (
                        <div
                          className="border-t border-gray-50 pt-4 mt-2 space-y-4 animate-in fade-in slide-in-from-top-2 duration-200"
                          onClick={(e) => e.stopPropagation()} // Prevent collapse when clicking details
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-gray-50/50 dark:bg-[#171717]/50 p-4 rounded-xl text-xs border border-gray-100 dark:border-gray-800">
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 flex-1">
                              <div>
                                <span className="text-gray-400 dark:text-gray-500 font-medium">Status</span>
                                <p className={`font-bold mt-0.5 ${run.status === 'failed' ? 'text-gray-500 dark:text-gray-400' : 'text-black dark:text-white'}`}>
                                  {run.status === 'failed' ? 'Execution Terminated' : 'Succeeded'}
                                </p>
                              </div>
                              <div>
                                <span className="text-gray-400 dark:text-gray-500 font-medium">Execution Cost</span>
                                <p className="font-bold text-slate-800 dark:text-slate-200 mt-0.5">
                                  <span className="inline-flex items-center px-2 py-0.5 bg-gray-100 dark:bg-[#212121] text-gray-800 dark:text-gray-205 rounded-md text-[10px] font-bold border border-gray-200 dark:border-gray-800">
                                    {Math.max(1, run.steps.filter(s => s.status !== 'skipped').length)} Credits
                                  </span>
                                </p>
                              </div>
                              <div className="col-span-2 sm:col-span-1">
                                <span className="text-gray-400 dark:text-gray-500 font-medium">Completed At</span>
                                <p className="font-semibold text-gray-800 dark:text-gray-200 mt-0.5">
                                  {new Date(run.runAt).toLocaleString('en-US', {
                                    dateStyle: 'medium',
                                    timeStyle: 'short'
                                  })}
                                </p>
                              </div>
                            </div>
                            <div className="shrink-0 flex items-center justify-end">
                              <button
                                onClick={(e) => handleDeleteLogClick(run.workflowId, run.id, e)}
                                className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white bg-white dark:bg-[#212121] hover:bg-gray-50 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-800 rounded-xl transition-all duration-200 shadow-sm active:scale-95 shrink-0"
                                title="Delete this execution log"
                              >
                                <Icons.Trash className="w-3.5 h-3.5" />
                                <span>Delete Log</span>
                              </button>
                            </div>
                          </div>

                          {/* AI Summary Block */}
                          {run.summary && (
                            <div className="p-5 bg-white dark:bg-[#171717] border border-gray-100 dark:border-gray-800 rounded-2xl flex flex-col gap-3 text-left shadow-sm">
                              <div className="flex items-center gap-2 text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-black dark:text-white animate-pulse">
                                  <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/>
                                  <path d="m5 3 1 2.5L8.5 6 6 7 5 9.5 4 7 1.5 6 4 5.5 5 3Z"/>
                                  <path d="m19 17 1 2.5 2.5.5-2.5 1-1 2.5-1-2.5-2.5-1 2.5-1 1-2.5Z"/>
                                </svg>
                                <span>AI Summary & Answer</span>
                              </div>
                              <div 
                                className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed font-sans font-medium markdown-content"
                                dangerouslySetInnerHTML={{ __html: formatMarkdownText(run.summary) }}
                              />
                            </div>
                          )}

                          <div className="space-y-3">
                            <h4 className="text-xs font-bold text-gray-400 dark:text-gray-550 uppercase tracking-widest">Execution Steps Log</h4>
                            {run.steps.map((step, idx) => {
                              const toolLow = step?.tool_name?.toLowerCase() || '';
                              let iconId = 'default';
                              if (toolLow.includes('email') || toolLow.includes('gmail')) iconId = 'gmail';
                              else if (toolLow.includes('calendar') || toolLow.includes('event')) iconId = 'calendar';
                              else if (toolLow.includes('sheet') || toolLow.includes('spreadsheet')) iconId = 'sheets';
                              else if (toolLow.includes('doc')) iconId = 'docs';
                              else if (toolLow.includes('drive') || toolLow.includes('file')) iconId = 'drive';
                              else if (toolLow.includes('notion')) iconId = 'notion';
                              const Icon = getAppIcon(iconId);

                              const outputText = step.outputText;
                              const isLastStep = idx === run.steps.length - 1;
                              const shouldCondense = run.steps.length > 1 && !isLastStep && isRetrievalTool(step?.tool_name);

                              const isEmailTool = toolLow.includes('gmail') || toolLow.includes('email');
                              let extractedUrl = null;
                              let appLabel = 'Open Link';
                              let AppIcon = Icons.ExternalLink;

                              if (isEmailTool) {
                                let emailId = '';
                                if (outputText) {
                                  try {
                                    const trimmed = outputText.trim();
                                    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
                                      const parsed = JSON.parse(trimmed);
                                      if (Array.isArray(parsed) && parsed.length > 0) {
                                        emailId = parsed[0].id || '';
                                      } else if (parsed && typeof parsed === 'object') {
                                        emailId = parsed.id || parsed.thread_id || parsed.message_id || '';
                                      }
                                    }
                                  } catch (e) {
                                    // ignore parsing errors
                                  }
                                }
                                extractedUrl = emailId 
                                  ? `https://mail.google.com/mail/u/0/#all/${emailId}` 
                                  : 'https://mail.google.com/mail/u/0/#inbox';
                                appLabel = 'Open in Gmail';
                                AppIcon = getAppIcon('gmail');
                              } else {
                                // Parse URL if present
                                const urlRegex = /(https?:\/\/[^\s<>\(\)]+)/;
                                const urlMatch = !shouldCondense && outputText ? outputText.match(urlRegex) : null;
                                extractedUrl = urlMatch ? urlMatch[0] : null;

                                if (extractedUrl) {
                                  const urlLower = extractedUrl.toLowerCase();
                                  if (urlLower.includes('docs.google.com/document')) {
                                    appLabel = 'Open in Google Docs';
                                    AppIcon = getAppIcon('docs');
                                  } else if (urlLower.includes('docs.google.com/spreadsheets')) {
                                    appLabel = 'Open in Google Sheets';
                                    AppIcon = getAppIcon('sheets');
                                  } else if (urlLower.includes('google.com/calendar')) {
                                    appLabel = 'Open in Google Calendar';
                                    AppIcon = getAppIcon('calendar');
                                  } else if (urlLower.includes('notion.so') || urlLower.includes('notion.site')) {
                                    appLabel = 'Open in Notion';
                                    AppIcon = getAppIcon('notion');
                                  } else if (urlLower.includes('mail.google.com')) {
                                    appLabel = 'Open in Gmail';
                                    AppIcon = getAppIcon('gmail');
                                  }
                                }
                              }

                              return (
                                <div key={step.id} className="flex gap-4 p-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50/20 dark:bg-[#171717]/20">
                                  <div className="w-8 h-8 rounded-lg bg-white dark:bg-[#212121] border border-gray-200 dark:border-gray-800 flex items-center justify-center font-bold text-sm shrink-0 shadow-sm text-gray-500 dark:text-gray-400">
                                    {step.step_order}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="flex items-center gap-2">
                                        <Icon className="w-4 h-4 shrink-0 text-slate-500 dark:text-slate-400" />
                                        <span className="font-bold text-sm text-gray-900 dark:text-white">{step?.tool_name}</span>
                                      </div>
                                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${
                                        step.status === 'failed' ? 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-red-950/20 dark:text-red-400 dark:border-red-900/30' :
                                        step.status === 'skipped' ? 'bg-slate-50 text-slate-500 border-slate-200 dark:bg-slate-900 dark:text-slate-400 dark:border-slate-800' :
                                        'bg-white text-black border-gray-200 dark:bg-black dark:text-white dark:border-gray-800'
                                      }`}>
                                        {step.status}
                                      </span>
                                    </div>

                                    {/* Dynamic Output Details */}
                                    <div className="mt-2">
                                      {step.status === 'skipped' ? (
                                        <div className="text-xs text-gray-400 dark:text-gray-500 italic bg-gray-50/50 dark:bg-[#171717] border border-gray-100 dark:border-gray-800 rounded-lg p-3 text-left">
                                          This step was skipped.
                                        </div>
                                      ) : shouldCondense ? (
                                        <div className="text-xs text-gray-400 dark:text-gray-500 italic bg-gray-50/50 dark:bg-[#171717] border border-gray-100 dark:border-gray-800 rounded-lg p-3 text-left">
                                          {getCondensedSummary(step?.tool_name)}
                                        </div>
                                      ) : (
                                        renderFormattedOutput(outputText)
                                      )}
                                    </div>

                                    {/* Branded Link Button */}
                                    {extractedUrl && (
                                      <div className="mt-3 flex">
                                        <a
                                          href={extractedUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="inline-flex items-center gap-2 px-4 py-2 bg-black dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-200 text-white dark:text-black rounded-xl text-xs font-semibold shadow-sm hover:shadow-md transition-all duration-200 transform hover:scale-[1.01] active:scale-[0.99]"
                                        >
                                          <AppIcon className="w-4 h-4 shrink-0 text-white" />
                                          <span>{appLabel}</span>
                                          <Icons.ExternalLink className="w-3 h-3 text-white/80" />
                                        </a>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="flex flex-col items-center justify-center py-20 gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-gray-50 dark:bg-[#171717] border border-gray-100/60 dark:border-white/5 flex items-center justify-center">
                    <Icons.Logs className="w-8 h-8 text-gray-300 dark:text-gray-600" />
                  </div>
                  <p className="text-sm text-gray-400 dark:text-gray-500">No execution logs yet. Logs will appear here after your first scheduled run.</p>
                </div>
              )}
            </div>
          ) : (
            // Pending Tab
            (() => {
              const pendingRuns = allRuns.filter(r => r.status === 'pending');
              if (pendingRuns.length === 0) {
                return (
                  <div className="flex flex-col items-center justify-center py-20 gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-gray-50 dark:bg-[#171717] border border-gray-100/60 dark:border-white/5 flex items-center justify-center">
                      <Icons.Shield className="w-8 h-8 text-gray-300 dark:text-gray-600" />
                    </div>
                    <div className="text-center max-w-sm">
                      <h3 className="text-lg font-normal text-gray-900 dark:text-white mb-2">No pending approvals</h3>
                      <p className="text-sm text-gray-400 dark:text-gray-500">
                        When a scheduled action requires your approval (like sending emails to clients), it will appear here for review.
                      </p>
                    </div>
                  </div>
                );
              }

              return (
                <div className="space-y-6">
                  {pendingRuns.map(run => {
                    // Find the step that is pending
                    const pendingStep = run.steps.find(s => s.status === 'pending');
                    if (!pendingStep) return null;

                    const toolLow = pendingStep.tool_name.toLowerCase();
                    let iconId = 'default';
                    if (toolLow.includes('email') || toolLow.includes('gmail')) iconId = 'gmail';
                    else if (toolLow.includes('calendar') || toolLow.includes('event')) iconId = 'calendar';
                    else if (toolLow.includes('sheet') || toolLow.includes('spreadsheet')) iconId = 'sheets';
                    else if (toolLow.includes('doc')) iconId = 'docs';
                    else if (toolLow.includes('drive') || toolLow.includes('file')) iconId = 'drive';
                    else if (toolLow.includes('notion')) iconId = 'notion';
                    const AppIcon = getAppIcon(iconId);

                    const args = pendingStep.args || {};
                    const isApproving = approvingId === run.id;

                    return (
                      <div
                        key={run.id}
                        className="p-6 rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-[#212121] shadow-sm flex flex-col gap-4 text-left"
                      >
                        {/* Header */}
                        <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-850 pb-4">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 rounded-xl bg-amber-50 dark:bg-amber-950/30 flex items-center justify-center shrink-0 border border-amber-100/50 dark:border-amber-900/30">
                              <Icons.ShieldAlert className="w-5 h-5 text-amber-500 dark:text-amber-400" />
                            </div>
                            <div className="min-w-0">
                              <h3 className="text-sm font-bold text-gray-950 dark:text-white truncate">{run.workflowName}</h3>
                              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                Run initiated {new Date(run.runAt).toLocaleString('en-US', {
                                  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                                })}
                              </p>
                            </div>
                          </div>
                          <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 px-2.5 py-1 rounded-full border border-amber-100 dark:border-amber-900/30 shrink-0">
                            Awaiting Approval
                          </span>
                        </div>

                        {/* Step details */}
                        <div className="flex flex-col gap-3">
                          <div className="flex items-center gap-2">
                            <span className="w-5 h-5 rounded-md bg-gray-50 dark:bg-gray-800 text-[10px] font-bold flex items-center justify-center border border-gray-100 dark:border-gray-750 text-gray-500 dark:text-gray-450 shrink-0">
                              {pendingStep.step_order}
                            </span>
                            <span className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider flex items-center gap-1.5">
                              <AppIcon className="w-4 h-4 shrink-0" />
                              {pendingStep.tool_name.replace(/_/g, ' ')}
                            </span>
                          </div>

                          {/* Arguments Preview Card */}
                          <div className="bg-gray-50/50 dark:bg-[#171717]/40 border border-gray-100 dark:border-gray-800/80 rounded-xl p-4 text-xs font-medium space-y-3">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-550 border-b border-gray-100 dark:border-gray-850 pb-1.5 mb-1.5">
                              Action Preview
                            </div>
                            {(toolLow.includes('email') || toolLow.includes('gmail')) && (args.to || args.subject || args.body) ? (
                              <div className="space-y-2">
                                {args.to && (
                                  <div className="flex gap-2">
                                    <span className="text-gray-400 dark:text-gray-500 w-12 shrink-0">To:</span>
                                    <span className="text-gray-950 dark:text-white font-semibold">{args.to}</span>
                                  </div>
                                )}
                                {args.subject && (
                                  <div className="flex gap-2">
                                    <span className="text-gray-400 dark:text-gray-500 w-12 shrink-0">Subject:</span>
                                    <span className="text-gray-950 dark:text-white font-semibold">{args.subject}</span>
                                  </div>
                                )}
                                {args.body && (
                                  <div className="flex gap-2 pt-1.5 border-t border-gray-100/50 dark:border-gray-850/50">
                                    <span className="text-gray-400 dark:text-gray-500 w-12 shrink-0">Body:</span>
                                    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed flex-1 font-sans">{args.body}</p>
                                  </div>
                                )}
                              </div>
                            ) : (toolLow.includes('calendar') || toolLow.includes('event')) && (args.title || args.summary || args.start || args.end) ? (
                              <div className="space-y-2">
                                {(args.title || args.summary) && (
                                  <div className="flex gap-2">
                                    <span className="text-gray-400 dark:text-gray-500 w-12 shrink-0">Event:</span>
                                    <span className="text-gray-950 dark:text-white font-semibold">{args.title || args.summary}</span>
                                  </div>
                                )}
                                {args.start && (
                                  <div className="flex gap-2">
                                    <span className="text-gray-400 dark:text-gray-500 w-12 shrink-0">Starts:</span>
                                    <span className="text-gray-700 dark:text-gray-300 font-semibold">{new Date(args.start).toLocaleString()}</span>
                                  </div>
                                )}
                                {args.end && (
                                  <div className="flex gap-2">
                                    <span className="text-gray-400 dark:text-gray-500 w-12 shrink-0">Ends:</span>
                                    <span className="text-gray-700 dark:text-gray-300 font-semibold">{new Date(args.end).toLocaleString()}</span>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <pre className="text-[11px] font-mono text-gray-600 dark:text-gray-400 overflow-x-auto bg-white dark:bg-[#151515] border dark:border-gray-850 p-3 rounded-lg leading-relaxed whitespace-pre-wrap">
                                {JSON.stringify(args, null, 2)}
                              </pre>
                            )}
                          </div>
                        </div>

                        {/* Action buttons */}
                        <div className="flex items-center gap-3 pt-2">
                          <button
                            onClick={() => handleApprovePendingStep(run.workflowId, pendingStep.id, run.id)}
                            disabled={isApproving}
                            className="flex items-center justify-center gap-2 px-5 py-2.5 bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-100 font-semibold text-xs rounded-xl shadow-sm hover:shadow transition-all disabled:opacity-50 active:scale-98 shrink-0 min-w-[120px]"
                          >
                            {isApproving ? (
                              <div className="w-3.5 h-3.5 border-2 border-gray-400 border-t-white dark:border-t-black rounded-full animate-spin"></div>
                            ) : (
                              <Icons.Check className="w-3.5 h-3.5" />
                            )}
                            <span>Approve Action</span>
                          </button>
                          
                          <button
                            onClick={() => handleRejectPendingStep(run.workflowId, pendingStep.id, run.id)}
                            disabled={isApproving}
                            className="flex items-center justify-center gap-2 px-5 py-2.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20 font-semibold text-xs border border-red-200 dark:border-red-900/35 rounded-xl transition-all disabled:opacity-50 active:scale-98 shrink-0"
                          >
                            <Icons.X className="w-3.5 h-3.5" />
                            <span>Reject & Cancel Run</span>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()
          )}

          {/* Failed Workflows Section (shown in Active tab) */}
          {activeTab === 'active' && failedWorkflows.length > 0 && (
            <div className="mt-12">
              <div className="flex items-center gap-2 mb-6 px-1">
                <span className="w-2.5 h-2.5 rounded-full bg-gray-400 dark:bg-gray-600"></span>
                <span className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Failed Tasks</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {failedWorkflows.map(workflow => (
                  <ScheduleWorkflowCard
                    key={workflow.id}
                    workflow={workflow}
                    onPauseResume={handlePauseResume}
                    onDelete={handleDeleteClick}
                    onSelect={handleSelectWorkflow}
                  />
                ))}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ─── Details / Edit Modal ─── */}
      {selectedWorkflow && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto animate-in fade-in">
          <div className="bg-white dark:bg-[#212121] rounded-3xl w-full max-w-2xl shadow-2xl border border-gray-100 dark:border-gray-800 flex flex-col overflow-hidden max-h-[90vh] animate-in zoom-in-95 duration-200">
            
            {/* Modal Header */}
            <div className="flex items-center justify-between px-8 py-6 border-b border-gray-100 dark:border-gray-850 flex-shrink-0">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                {isEditing ? 'Edit Schedule' : 'Schedule Details'}
              </h3>
              <button
                onClick={() => { setSelectedWorkflow(null); setIsEditing(false); }}
                className="p-1.5 text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#171717] rounded-full transition-all"
              >
                <Icons.X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-8 space-y-6 no-scrollbar">
              {isEditing ? (
                /* EDIT MODE FORM */
                <div className="space-y-5">
                  {/* Name */}
                  <div>
                    <label className="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2">Schedule Name</label>
                    <input
                      type="text"
                      value={editName}
                      onChange={e => setEditName(e.target.value)}
                      className="w-full bg-white dark:bg-[#171717] border border-gray-200 dark:border-gray-805 rounded-xl px-4 py-2.5 text-sm font-medium focus:border-black dark:focus:border-white outline-none transition-all focus:ring-1 focus:ring-black dark:focus:ring-white dark:text-white"
                      placeholder="Enter a descriptive name"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-xs font-bold text-gray-400 dark:text-gray-550 uppercase tracking-widest mb-2">Description</label>
                    <textarea
                      value={editDescription}
                      onChange={e => setEditDescription(e.target.value)}
                      rows={2}
                      className="w-full bg-white dark:bg-[#171717] border border-gray-200 dark:border-gray-805 rounded-xl px-4 py-2.5 text-sm font-medium focus:border-black dark:focus:border-white outline-none transition-all focus:ring-1 focus:ring-black dark:focus:ring-white resize-none dark:text-white"
                      placeholder="Describe what this workflow does"
                    />
                  </div>

                  {/* Repeat / Timing */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-400 dark:text-gray-550 uppercase tracking-widest mb-2">Repeat Frequency</label>
                      <select
                        value={editFrequency}
                        onChange={e => setEditFrequency(e.target.value)}
                        className="w-full bg-white dark:bg-[#171717] border border-gray-200 dark:border-gray-805 rounded-xl px-4 py-2.5 text-sm font-medium focus:border-black dark:focus:border-white outline-none transition-all focus:ring-1 focus:ring-black dark:focus:ring-white dark:text-white"
                      >
                        <option value="hourly">Every Hour</option>
                        <option value="daily">Every Day</option>
                        <option value="weekly">Every Week</option>
                        <option value="custom">Custom Cron Expression</option>
                      </select>
                    </div>

                    {editFrequency === 'weekly' && (
                      <div>
                        <label className="block text-xs font-bold text-gray-400 dark:text-gray-550 uppercase tracking-widest mb-2">Day of the Week</label>
                        <select
                          value={editDayOfWeek}
                          onChange={e => setEditDayOfWeek(e.target.value)}
                          className="w-full bg-white dark:bg-[#171717] border border-gray-200 dark:border-gray-850 rounded-xl px-4 py-2.5 text-sm font-medium focus:border-black dark:focus:border-white outline-none transition-all focus:ring-1 focus:ring-black dark:focus:ring-white dark:text-white"
                        >
                          <option value="1">Monday</option>
                          <option value="2">Tuesday</option>
                          <option value="3">Wednesday</option>
                          <option value="4">Thursday</option>
                          <option value="5">Friday</option>
                          <option value="6">Saturday</option>
                          <option value="0">Sunday</option>
                        </select>
                      </div>
                    )}

                    {(editFrequency === 'daily' || editFrequency === 'weekly') && (
                      <div>
                        <label className="block text-xs font-bold text-gray-400 dark:text-gray-550 uppercase tracking-widest mb-2">Execution Time</label>
                        <input
                          type="time"
                          value={editTime}
                          onChange={e => setEditTime(e.target.value)}
                          className="w-full bg-white dark:bg-[#171717] border border-gray-200 dark:border-gray-850 rounded-xl px-4 py-2.5 text-sm font-medium focus:border-black dark:focus:border-white outline-none transition-all focus:ring-1 focus:ring-black dark:focus:ring-white dark:text-white"
                        />
                      </div>
                    )}

                    {editFrequency === 'custom' && (
                      <div className="md:col-span-2">
                        <label className="block text-xs font-bold text-gray-400 dark:text-gray-550 uppercase tracking-widest mb-2">Cron Expression</label>
                        <input
                          type="text"
                          value={editCron}
                          onChange={e => setEditCron(e.target.value)}
                          className="w-full bg-white dark:bg-[#171717] border border-gray-200 dark:border-gray-850 rounded-xl px-4 py-2.5 text-sm font-medium font-mono focus:border-black dark:focus:border-white outline-none transition-all focus:ring-1 focus:ring-black dark:focus:ring-white dark:text-white"
                          placeholder="e.g. 0 9 * * 1-5"
                        />
                        <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1.5 font-medium">Format: minute hour day-of-month month day-of-week</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                /* READ-ONLY VIEW MODE */
                <div className="space-y-6">
                  {/* Header / Meta */}
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h4 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight leading-snug">{selectedWorkflow.name}</h4>
                      {selectedWorkflow.description && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 leading-relaxed">{selectedWorkflow.description}</p>
                      )}
                    </div>
                    
                    {/* Status Badge */}
                    <div className="shrink-0">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider ${
                        selectedWorkflow.status === 'active' ? 'bg-gray-100 text-black border border-gray-200 dark:bg-[#171717] dark:text-white dark:border-gray-800' :
                        selectedWorkflow.status === 'paused' ? 'bg-gray-50 text-gray-600 border border-gray-200 dark:bg-[#171717]/50 dark:text-gray-400 dark:border-gray-800' :
                        'bg-gray-200 text-black border border-gray-300 dark:bg-gray-800 dark:text-white dark:border-gray-700'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          selectedWorkflow.status === 'active' ? 'bg-black dark:bg-white animate-pulse' :
                          selectedWorkflow.status === 'paused' ? 'bg-gray-400 dark:bg-gray-650' : 'bg-gray-600 dark:bg-gray-300'
                        }`}></span>
                        {selectedWorkflow.status}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50/50 dark:bg-[#171717]/50 border border-gray-100 dark:border-gray-800 rounded-2xl p-4 text-sm font-medium">
                    <div className="flex items-center gap-2.5 text-gray-600 dark:text-gray-300">
                      <Icons.Repeat className="w-4 h-4 text-slate-400" />
                      <span>Runs: <strong className="text-gray-900 dark:text-white">{selectedWorkflow.cron_expression ? cronToHumanReadable(selectedWorkflow.cron_expression) : 'Event Triggered'}</strong></span>
                    </div>
                    {selectedWorkflow.cron_expression && (
                      <div className="flex items-center gap-2.5 text-gray-600 dark:text-gray-300">
                        <Icons.Clock className="w-4 h-4 text-slate-400" />
                        <span className="font-mono">Cron: <strong className="text-gray-900 dark:text-white">{selectedWorkflow.cron_expression}</strong></span>
                      </div>
                    )}
                  </div>

                  {/* Workflow Steps */}
                  <div className="space-y-4">
                    <h5 className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Workflow Steps</h5>
                    <div className="space-y-3">
                      {(selectedWorkflow?.steps || []).map((step, idx) => {
                        const toolLow = step?.tool_name?.toLowerCase() || '';
                        let iconId = 'default';
                        if (toolLow.includes('email') || toolLow.includes('gmail')) iconId = 'gmail';
                        else if (toolLow.includes('calendar') || toolLow.includes('event')) iconId = 'calendar';
                        else if (toolLow.includes('sheet') || toolLow.includes('spreadsheet')) iconId = 'sheets';
                        else if (toolLow.includes('doc')) iconId = 'docs';
                        else if (toolLow.includes('drive') || toolLow.includes('file')) iconId = 'drive';
                        else if (toolLow.includes('notion')) iconId = 'notion';
                        const Icon = getAppIcon(iconId);

                        return (
                          <div key={step.id} className="flex items-start gap-4 p-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50/20 dark:bg-[#171717]/20">
                            <div className="w-8 h-8 rounded-lg bg-white dark:bg-[#212121] border border-gray-200 dark:border-gray-800 flex items-center justify-center font-bold text-sm shrink-0 shadow-sm text-gray-500 dark:text-gray-400">
                              {step.step_order}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <Icon className="w-4 h-4 shrink-0 text-slate-500 dark:text-slate-400" />
                                <span className="font-bold text-sm text-gray-900 dark:text-white">{step?.tool_name}</span>
                              </div>
                              {step?.args && Object.keys(step.args).length > 0 && (
                                <pre className="text-xs text-gray-500 dark:text-gray-400 bg-white dark:bg-[#171717] border border-gray-100 dark:border-gray-800 rounded-lg p-3 mt-2 overflow-x-auto max-w-full font-mono">
                                  {JSON.stringify(step.args, null, 2)}
                                </pre>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-8 py-6 border-t border-gray-100 dark:border-gray-855 flex items-center justify-between flex-shrink-0 bg-gray-50/30 dark:bg-[#171717]/30">
              {isEditing ? (
                <>
                  <button
                    onClick={() => setIsEditing(false)}
                    className="px-5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-800 text-sm font-semibold hover:bg-gray-55 text-gray-700 dark:text-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveChanges}
                    disabled={isSubmitting}
                    className="px-6 py-2.5 bg-black dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-200 text-white dark:text-black rounded-xl text-sm font-semibold shadow-md transition-all flex items-center gap-2"
                  >
                    {isSubmitting ? 'Saving...' : 'Save Changes'}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => handleDeleteClick(selectedWorkflow.id)}
                    className="px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-700 dark:text-gray-400 hover:text-black dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#171717] transition-colors flex items-center gap-1.5"
                  >
                    <Icons.Trash className="w-4 h-4 text-gray-500" />
                    <span>Delete</span>
                  </button>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setIsEditing(true)}
                      className="px-5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-[#171717] text-sm font-semibold text-gray-700 dark:text-gray-400 transition-colors flex items-center gap-1.5"
                    >
                      <Icons.Pencil className="w-4 h-4 text-gray-500" />
                      <span>Edit Settings</span>
                    </button>
                    <button
                      onClick={() => setSelectedWorkflow(null)}
                      className="px-6 py-2.5 bg-black dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-200 text-white dark:text-black rounded-xl text-sm font-semibold shadow-md transition-all"
                    >
                      Done
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── Delete Confirmation Modal ─── */}
      {workflowToDelete && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 z-50 flex items-center justify-center animate-in fade-in">
          <div className="bg-white dark:bg-[#121212] rounded-2xl p-6 w-full max-w-sm mx-4 shadow-xl border border-gray-100 dark:border-white/10 flex flex-col gap-4">
            <h3 className="text-xl font-bold text-black dark:text-white">Delete Schedule</h3>
            <p className="text-black dark:text-gray-200 text-sm">
              Are you sure you want to delete the schedule <strong>{workflowToDelete.name}</strong>?
            </p>
            <p className="text-gray-500 dark:text-gray-400 text-xs">
              This will permanently stop this autonomous workflow.
            </p>
            <div className="flex items-center gap-3 mt-4">
              <button
                onClick={() => setWorkflowToDelete(null)}
                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-800 text-black dark:text-gray-400 font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await handleDelete(workflowToDelete.id);
                  setWorkflowToDelete(null);
                }}
                className="flex-1 px-4 py-2.5 rounded-xl bg-black dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-200 text-white dark:text-black font-medium transition-colors border border-transparent"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Delete Log Run Confirmation Modal ─── */}
      {logToDelete && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 z-50 flex items-center justify-center animate-in fade-in">
          <div className="bg-white dark:bg-[#212121] rounded-2xl p-6 w-full max-w-sm mx-4 shadow-xl border border-gray-100 dark:border-gray-855 flex flex-col gap-4">
            <h3 className="text-xl font-bold text-black dark:text-white">Delete Log Run</h3>
            <p className="text-black dark:text-gray-200 text-sm">
              Are you sure you want to delete this workflow execution log?
            </p>
            <p className="text-gray-500 dark:text-gray-400 text-xs">
              This will permanently remove the execution steps history for this specific run.
            </p>
            <div className="flex items-center gap-3 mt-4">
              <button
                onClick={() => setLogToDelete(null)}
                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-800 text-black dark:text-gray-400 font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const { workflowId, runId } = logToDelete;
                  setLogToDelete(null); // Close modal
                  await executeDeleteLog(workflowId, runId);
                }}
                className="flex-1 px-4 py-2.5 rounded-xl bg-black dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-200 text-white dark:text-black font-medium transition-colors border border-transparent"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchedulesDashboard;
